import type { ComponentType } from "react";
import type { LucideIcon } from "lucide-react";

/**
 * The dashboard's widget contract.
 *
 * This file is the ONLY place that knows which widgets exist. The grid, the
 * layout store and the edit UI all consume this list and never name a concrete
 * widget — so adding one is a single entry here (open-closed), and nothing that
 * arranges or persists widgets has to change.
 */

/** Column spans on the 4-column dashboard grid. Not pixels — see CLAUDE.md. */
export const WIDGET_SIZES = ["sm", "md", "lg", "full"] as const;
export type WidgetSize = (typeof WIDGET_SIZES)[number];

export const WIDGET_SIZE_LABELS: Record<WidgetSize, string> = {
  sm:   "Small",
  md:   "Medium",
  lg:   "Large",
  full: "Full width",
};

// Static class strings, so Tailwind's scanner can see every one of them. A
// computed `col-span-${n}` would be purged from the build.
//
// Spans are out of SIX tracks, not four: a quarter-width card was too narrow for
// anything with internal structure (the attendance calendar wrapped its own date
// range onto three lines), so "Small" is a third and the scale reads
// third / half / two-thirds / full.
export const WIDGET_SIZE_CLASS: Record<WidgetSize, string> = {
  sm:   "md:col-span-2",
  md:   "md:col-span-3",
  lg:   "md:col-span-4",
  full: "md:col-span-6",
};

/**
 * What a widget's own component receives. Deliberately empty of layout concerns:
 * a widget cannot see whether it is being dragged, what size it is, or how it is
 * stored (interface segregation). WidgetFrame owns all of that.
 */
export interface WidgetProps {
  /** The signed-in employee, for widgets that need their own data. */
  employeeId: string;
  role: string;
}

export interface DashboardWidgetDef {
  /** Stable storage key. Never rename — a rename orphans everyone's saved row. */
  key: string;
  title: string;
  icon: LucideIcon;
  /** One line in the "Add widget" list explaining what it shows. */
  description: string;
  component: ComponentType<WidgetProps>;
  defaultSize: WidgetSize;
  /** Position when the employee has no saved layout. Lower comes first. */
  defaultOrder: number;
  /** Off by default; the employee opts in from "Add widget". */
  defaultHidden?: boolean;
  /** Sizes this widget actually works at. Omit to allow all four. */
  allowedSizes?: readonly WidgetSize[];
  /**
   * Whether this widget applies to the viewer at all. A widget that returns
   * false is not rendered, not listed, and not persisted — distinct from one the
   * employee has *hidden*, which they can bring back.
   */
  isAvailable?: (ctx: WidgetProps) => boolean;
  /** Blocks removal for widgets the dashboard would be meaningless without. */
  required?: boolean;
}

/** One stored arrangement entry. Mirrors the API row. */
export interface WidgetLayoutEntry {
  widgetKey: string;
  sortOrder: number;
  size: WidgetSize;
  isHidden: boolean;
}

/** A widget paired with the layout currently applied to it. */
export interface ResolvedWidget {
  def: DashboardWidgetDef;
  size: WidgetSize;
  isHidden: boolean;
}

export function sizesFor(def: DashboardWidgetDef): readonly WidgetSize[] {
  return def.allowedSizes ?? WIDGET_SIZES;
}

/**
 * Merges the registry with a saved layout into the list the grid renders.
 *
 * The registry is the source of truth for what EXISTS; the saved layout only
 * supplies order, size and visibility. That split is what lets a newly shipped
 * widget appear for everyone with no backfill, and lets a removed widget's stale
 * rows be ignored rather than crash.
 */
export function resolveLayout(
  registry: DashboardWidgetDef[],
  saved: WidgetLayoutEntry[],
  ctx: WidgetProps,
): ResolvedWidget[] {
  const byKey = new Map(saved.map((e) => [e.widgetKey, e]));

  return registry
    .filter((def) => def.isAvailable?.(ctx) ?? true)
    .map((def) => {
      const entry = byKey.get(def.key);
      const size = entry && (WIDGET_SIZES as readonly string[]).includes(entry.size)
        ? entry.size
        : def.defaultSize;
      return {
        def,
        size,
        isHidden: entry ? entry.isHidden : (def.defaultHidden ?? false),
        // Saved order wins; an unsaved (newly shipped) widget falls back to its
        // registry position rather than jumping to the top.
        _order: entry ? entry.sortOrder : def.defaultOrder,
      };
    })
    .sort((a, b) => a._order - b._order)
    .map(({ _order, ...w }) => w);
}

/** The arrangement to persist for the current on-screen state. */
export function toLayoutEntries(widgets: ResolvedWidget[]): WidgetLayoutEntry[] {
  return widgets.map((w, i) => ({
    widgetKey: w.def.key,
    sortOrder: i,
    size:      w.size,
    isHidden:  w.isHidden,
  }));
}
